"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Mail, MessageSquare, ArrowRight } from "lucide-react";
import { sendVerificationEmail } from "supertokens-web-js/recipe/emailverification";
import { BiLogoGmail } from "react-icons/bi";
import { PiMicrosoftOutlookLogo } from "react-icons/pi";
import { FiCloud } from "react-icons/fi";
import AuthLayout from "./AuthLayout";

export default function VerificationCard() {
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const emailProviders = [
    {
      name: "Gmail",
      url: "https://mail.google.com",
      icon: BiLogoGmail,
      color: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-500/10",
      hoverBg: "hover:bg-red-100 dark:hover:bg-red-500/20",
    },
    {
      name: "Outlook",
      url: "https://outlook.office.com",
      icon: PiMicrosoftOutlookLogo,
      color: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-500/10",
      hoverBg: "hover:bg-blue-100 dark:hover:bg-blue-500/20",
    },
    {
      name: "iCloud Mail",
      url: "https://www.icloud.com/mail",
      icon: FiCloud,
      color: "text-gray-500",
      bgColor: "bg-gray-50 dark:bg-gray-500/10",
      hoverBg: "hover:bg-gray-100 dark:hover:bg-gray-500/20",
    },
  ];

  useEffect(() => {
    sendEmail();
  }, []);

  async function sendEmail() {
    setIsSending(true);
    try {
      const response = await sendVerificationEmail();
      if (response.status === "EMAIL_ALREADY_VERIFIED_ERROR") {
        window.location.assign("/home");
      } else {
        setMessage(
          "We've sent you a verification email. Please check your inbox."
        );
      }
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        setMessage(err.message);
      } else {
        setMessage("Oops! Something went wrong.");
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <AuthLayout>
      {/* Left side - Auth */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="rounded-full bg-[#4A90E2]/10 p-3">
                <Mail className="h-6 w-6 text-[#4A90E2]" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-semibold tracking-tight">
                  Verify Your Email
                </h2>
              </div>
            </div>

            {message && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-800 p-6 space-y-6">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {message}
                </p>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Open your email client
                  </h3>
                  <div className="grid gap-3">
                    {emailProviders.map((provider) => (
                      <a
                        key={provider.name}
                        href={provider.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-between ${provider.bgColor} ${provider.hoverBg} 
                          p-4 rounded-lg transition-all duration-200 group`}
                      >
                        <div className="flex items-center space-x-3">
                          <provider.icon
                            className={`h-5 w-5 ${provider.color}`}
                          />
                          <span className={`font-medium ${provider.color}`}>
                            {provider.name}
                          </span>
                        </div>
                        <ArrowRight
                          className={`h-4 w-4 ${provider.color} opacity-0 group-hover:opacity-100 
                          transition-opacity duration-200`}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={sendEmail}
              disabled={isSending}
              className="w-full bg-[#4A90E2] hover:bg-[#357ABD] transition-colors duration-200"
            >
              {isSending ? "Sending..." : "Resend Verification Email"}
            </Button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
